import React, { useEffect, useState } from "react";
import careersbanner from "../assets/images/page-titles/6.webp";
import { Link } from "react-router-dom";
import TechnologySlider from "../components/TechnologySlider";
import {
  X,
  Briefcase,
  Upload,
  Loader,
  CheckCircle,
  ChevronDown,
} from "lucide-react";
import axios from "axios";
import DOMPurify from "dompurify";
import banner_image_1 from "../assets/images/about/careers_img.jpg";

function Careers() {
  const api_url =
    localStorage.getItem("api_url") || "http://localhost/inscape_backend/api/";
  const company_id = localStorage.getItem("company_id") || "2";

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openJobId, setOpenJobId] = useState(null);

  const [applyModal, setApplyModal] = useState({ open: false, job: null });
  const [formData, setFormData] = useState({
    candidate_full_name: "",
    candidate_email_address: "",
    candidate_phone_no: "",
    candidate_resume: null,
  });
  const [resumeFileName, setResumeFileName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  useEffect(() => {
    document.querySelectorAll(".bg-img")?.forEach((el) => {
      const img = el.querySelector("img");
      if (img) {
        const parent = el.parentElement;
        if (parent) {
          parent.style.backgroundImage = `url(${img.src})`;
          parent.style.backgroundSize = "cover";
          parent.style.backgroundPosition = "center";
          parent.classList.add("bg-img");
        }
        el.remove();
      }
    });
    fetchActiveJobs();
  }, []);

  const fetchActiveJobs = async () => {
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("company_id", company_id);
      fd.append("employee_id", "1");
      fd.append("type", "8");
      const response = await fetch(`${api_url}job_recruitment.php`, {
        method: "POST",
        body: fd,
      });
      const data = await response.json();
      setJobs(data.status === "1" ? data.result || [] : []);
    } catch (err) {
      console.error("Fetch error:", err);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleJob = (id) => {
    setOpenJobId((prev) => (prev === id ? null : id));
  };

  const openApplyModal = (job) => {
    setApplyModal({ open: true, job });
    setFormData({
      candidate_full_name: "",
      candidate_email_address: "",
      candidate_phone_no: "",
      candidate_resume: null,
    });
    setResumeFileName("");
    setMessage({ type: "", text: "" });
  };

  const closeApplyModal = () => {
    setApplyModal({ open: false, job: null });
    setFormData({
      candidate_full_name: "",
      candidate_email_address: "",
      candidate_phone_no: "",
      candidate_resume: null,
    });
    setResumeFileName("");
    setMessage({ type: "", text: "" });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setMessage({ type: "", text: "" });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!allowedTypes.includes(file.type)) {
      setMessage({
        type: "error",
        text: "Please upload a valid resume (PDF, DOC, DOCX)",
      });
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: "error", text: "File size must not exceed 5MB" });
      e.target.value = "";
      return;
    }
    setFormData((prev) => ({ ...prev, candidate_resume: file }));
    setResumeFileName(file.name);
    setMessage({ type: "", text: "" });
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    if (!formData.candidate_full_name.trim()) {
      setMessage({ type: "error", text: "Full name is required" });
      return;
    }
    if (!formData.candidate_email_address.trim()) {
      setMessage({ type: "error", text: "Email address is required" });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.candidate_email_address)) {
      setMessage({ type: "error", text: "Please enter a valid email address" });
      return;
    }
    if (!formData.candidate_phone_no.trim()) {
      setMessage({ type: "error", text: "Phone number is required" });
      return;
    }
    const phoneRegex = /^[0-9]{10,15}$/;
    if (!phoneRegex.test(formData.candidate_phone_no.replace(/[\s-]/g, ""))) {
      setMessage({
        type: "error",
        text: "Please enter a valid phone number (10–15 digits)",
      });
      return;
    }
    if (!formData.candidate_resume) {
      setMessage({ type: "error", text: "Resume is required" });
      return;
    }

    setSubmitting(true);
    setMessage({ type: "", text: "" });

    try {
      const submitData = new FormData();
      submitData.append("type", "3");
      submitData.append(
        "candidate_job_position",
        applyModal.job.job_recruitment_id,
      );
      submitData.append("candidate_full_name", formData.candidate_full_name);
      submitData.append(
        "candidate_email_address",
        formData.candidate_email_address,
      );
      submitData.append("candidate_phone_no", formData.candidate_phone_no);
      submitData.append("candidate_resume", formData.candidate_resume);

      const response = await axios.post(`${api_url}candidate.php`, submitData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.status === "1") {
        closeApplyModal();
        setShowSuccessDialog(true);
      } else {
        setMessage({
          type: "error",
          text: response.data.error || "Failed to submit application",
        });
      }
    } catch (error) {
      console.error("Submit error:", error);
      setMessage({
        type: "error",
        text: "An error occurred while submitting your application",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Banner */}
      <section className="page-title-layout1 page-title-light bg-parallax bg-overlay text-center">
        <div className="bg-img">
          <img src={careersbanner} alt="background" />
        </div>
        <div className="container">
          <div className="row">
            <div className="col-sm-12 col-md-12 col-lg-6 offset-lg-3">
              <h1 className="pagetitle-heading">Careers</h1>
              <nav>
                <ol className="breadcrumb justify-content-center mb-0">
                  <li className="breadcrumb-item">
                    <Link to="/">Home</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Careers
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </section>

      {/* Careers Section */}
      <section className="careers">
        <div className="container">
          {/* Intro */}
          <div className="row heading-layout2 mb-60">
            <div className="col-sm-12 col-md-12 col-lg-6">
              <h2 className="heading-subtitle">
                Ready to elevate your career?
              </h2>
              <h3 className="heading-title pr-50">
                Your talent, ideas, and perspectives matter here!
              </h3>
            </div>
            <div className="col-sm-12 col-md-12 col-lg-6">
              <p className="heading-desc fw-bold color-secondary mb-20">
                At Inscape, we believe our people are our greatest strength. We
                are always looking for motivated, skilled, and passionate
                professionals who want to build meaningful careers while working
                with global accounting practices.
              </p>
              <p className="heading-desc">
                If you value learning, ownership, and long-term growth—and want
                to be part of a team that works as a true partner to its
                clients—Inscape is the place for you.
              </p>
            </div>
          </div>

          {/* Life at Inscape */}
          <section
            className="banner-layout1 pt-40 pb-80"
            style={{ marginTop: "-100px" }}
          >
            <div className="container">
              <div className="row row-has-bg">
                <div className="col-sm-12 col-md-12 col-lg-6 col-xl-6">
                  <div className="banner-img animated-arrow-hover mt-50 mx-0">
                    <img
                      src={banner_image_1}
                      alt="Life at Inscape"
                      className="w-100"
                      loading="lazy"
                      style={{
                        borderRadius: "10px",
                        boxShadow: "0 3px 8px rgba(0,0,0,0.15)",
                      }}
                    />
                    <svg
                      className="animated-arrow rotate-90"
                      xmlns="http://www.w3.org/2000/svg"
                      width={160}
                      height={160}
                      viewBox="0 0 101.7 101.7"
                    >
                      <g fill="none" stroke="currentColor" strokeWidth={6}>
                        <path d="m.7 101 100-100" strokeLinejoin="round" />
                        <path
                          d="M.7 1h100"
                          strokeWidth={9}
                          strokeLinejoin="round"
                        />
                        <path
                          d="M100.7 1v100"
                          strokeWidth={9}
                          strokeLinejoin="round"
                        />
                      </g>
                    </svg>
                  </div>
                </div>
                <div className="col-sm-12 col-md-12 col-lg-6 col-xl-5 offset-xl-1">
                  <div className="banner-text mt-50 mw-500">
                    <div className="heading-layout2">
                      <h3
                        className="heading-title mb-30"
                        style={{ fontSize: 28 }}
                      >
                        Life at Inscape
                      </h3>
                    </div>
                    <ul className="list-items-layout1 d-inline-flex flex-column list-unstyled mb-30">
                      <li className="list-item fw-bold">
                        Work on core accounting and taxation—not just data
                        processing
                      </li>
                      <li className="list-item fw-bold">
                        Gain real exposure to standards, tax provisions, and
                        technical analysis
                      </li>
                      <li className="list-item fw-bold">
                        Learn with structure, clarity, and strong professional
                        discipline
                      </li>
                      <li className="list-item fw-bold">
                        Grow faster through hands-on responsibility and
                        continuous mentoring
                      </li>
                      <li className="list-item fw-bold">
                        No shortcuts—quality, accuracy, and compliance come
                        first
                      </li>
                      <li className="list-item fw-bold">
                        Build expertise that creates real impact for businesses
                        and individuals
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Job Openings — Accordion */}
          <div className="row">
            <div className="col-12">
              <h3 style={styles.sectionTitle}>Current openings</h3>

              {loading ? (
                <div style={styles.centerBox}>
                  <Loader
                    size={40}
                    style={{
                      color: "#ff6600",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                  <p style={{ marginTop: 12, color: "#888" }}>
                    Loading job openings...
                  </p>
                </div>
              ) : jobs.length === 0 ? (
                <div style={styles.centerBox}>
                  <Briefcase size={40} style={{ color: "#ccc" }} />
                  <p style={{ marginTop: 12, color: "#888" }}>
                    No job openings available at the moment.
                  </p>
                </div>
              ) : (
                <div style={styles.accordionList}>
                  {jobs.map((job) => {
                    const isOpen = openJobId === job.job_recruitment_id;
                    return (
                      <div
                        key={job.job_recruitment_id}
                        style={{
                          ...styles.accordionCard,
                          borderColor: isOpen ? "#ff6600" : "#e0e0e0",
                        }}
                      >
                        {/* Header row */}
                        <button
                          onClick={() => toggleJob(job.job_recruitment_id)}
                          aria-expanded={isOpen}
                          style={styles.accordionHeader}
                        >
                          <div style={styles.headerLeft}>
                            <div style={styles.jobIconBox}>
                              <Briefcase size={18} color="#ff6600" />
                            </div>
                            <span style={styles.jobTitle}>
                              {job.job_position}
                            </span>
                          </div>
                          <div style={styles.headerRight}>
                            {job.job_type && (
                              <span style={styles.badge}>{job.job_type}</span>
                            )}
                            <ChevronDown
                              size={20}
                              style={{
                                color: isOpen ? "#ff6600" : "#aaa",
                                transition: "transform 0.25s ease",
                                transform: isOpen
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)",
                                flexShrink: 0,
                              }}
                            />
                          </div>
                        </button>

                        {/* Body — description + apply */}
                        {isOpen && (
                          <div style={styles.accordionBody}>
                            <p style={styles.descLabel}>About this role</p>
                            <div
                              className="job-description"
                              style={styles.descContent}
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(
                                  job.job_description.replace(/&nbsp;/g, " "),
                                ),
                              }}
                            />
                            <button
                              onClick={() => openApplyModal(job)}
                              style={styles.applyBtn}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#e65c00";
                                e.currentTarget.style.transform =
                                  "translateY(-1px)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#ff6600";
                                e.currentTarget.style.transform =
                                  "translateY(0)";
                              }}
                            >
                              Apply Now
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <TechnologySlider />

        {/* Apply Modal */}
        {applyModal.open && (
          <div style={styles.overlay} onClick={closeApplyModal}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
              {/* Modal Header */}
              <div style={styles.modalHeader}>
                <div>
                  <h2 style={styles.modalTitle}>Apply for position</h2>
                  <p style={styles.modalSub}>{applyModal.job?.job_position}</p>
                </div>
                <button
                  onClick={closeApplyModal}
                  style={styles.closeBtn}
                  aria-label="Close"
                >
                  <X size={20} color="#666" />
                </button>
              </div>

              {/* Modal Body */}
              <div style={styles.modalBody}>
                {message.text && (
                  <div
                    style={{
                      ...styles.alertBox,
                      backgroundColor:
                        message.type === "error" ? "#f8d7da" : "#d4edda",
                      color: message.type === "error" ? "#721c24" : "#155724",
                      borderColor:
                        message.type === "error" ? "#f5c6cb" : "#c3e6cb",
                    }}
                  >
                    {message.text}
                  </div>
                )}

                <form onSubmit={handleSubmitApplication}>
                  {[
                    {
                      label: "Full Name",
                      name: "candidate_full_name",
                      type: "text",
                      placeholder: "Your full name",
                    },
                    {
                      label: "Email Address",
                      name: "candidate_email_address",
                      type: "email",
                      placeholder: "you@example.com",
                    },
                    {
                      label: "Phone Number",
                      name: "candidate_phone_no",
                      type: "tel",
                      placeholder: "10-digit number",
                      maxLength: 10,
                    },
                  ].map((field) => (
                    <div key={field.name} style={styles.formGroup}>
                      <label style={styles.formLabel}>
                        {field.label} <span style={{ color: "red" }}>*</span>
                      </label>
                      <input
                        type={field.type}
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleInputChange}
                        placeholder={field.placeholder}
                        disabled={submitting}
                        maxLength={field.maxLength}
                        style={styles.formInput}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#ff6600")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#e0e0e0")}
                      />
                    </div>
                  ))}

                  {/* Resume Upload */}
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>
                      Resume <span style={{ color: "red" }}>*</span>
                    </label>
                    <div
                      style={{
                        ...styles.fileDrop,
                        backgroundColor: resumeFileName ? "#f9f9f9" : "white",
                      }}
                      onClick={() =>
                        !submitting &&
                        document.getElementById("resume-upload").click()
                      }
                      onMouseEnter={(e) => {
                        if (!submitting) {
                          e.currentTarget.style.borderColor = "#ff6600";
                          e.currentTarget.style.backgroundColor = "#fff9f5";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "#e0e0e0";
                        e.currentTarget.style.backgroundColor = resumeFileName
                          ? "#f9f9f9"
                          : "white";
                      }}
                    >
                      <input
                        id="resume-upload"
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                        disabled={submitting}
                        style={{ display: "none" }}
                      />
                      <Upload
                        size={32}
                        style={{ color: "#ff6600", marginBottom: 8 }}
                      />
                      <p
                        style={{
                          margin: "0 0 4px",
                          fontSize: 14,
                          color: "#333",
                          fontWeight: 500,
                        }}
                      >
                        {resumeFileName || "Click to upload your resume"}
                      </p>
                      <p style={{ margin: 0, fontSize: 12, color: "#999" }}>
                        PDF, DOC, DOCX — max 5MB
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={styles.modalFooter}>
                    <button
                      type="button"
                      onClick={closeApplyModal}
                      disabled={submitting}
                      style={styles.cancelBtn}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      style={{
                        ...styles.submitBtn,
                        opacity: submitting ? 0.6 : 1,
                      }}
                    >
                      {submitting ? (
                        <>
                          <Loader
                            size={15}
                            style={{
                              animation: "spin 1s linear infinite",
                              color: "white",
                            }}
                          />
                          Submitting...
                        </>
                      ) : (
                        "Submit Application"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Success Dialog */}
        {showSuccessDialog && (
          <div style={styles.overlay}>
            <div style={styles.successBox}>
              <div style={styles.successIcon}>
                <CheckCircle size={40} color="#28a745" />
              </div>
              <h3 style={{ margin: "0 0 10px", color: "#28a745" }}>
                Application Submitted!
              </h3>
              <p
                style={{
                  margin: "0 0 24px",
                  color: "#666",
                  fontSize: 15,
                  lineHeight: 1.6,
                }}
              >
                Thank you for applying! We have received your application and
                will review it shortly. You will be contacted if your profile
                matches our requirements.
              </p>
              <button
                onClick={() => setShowSuccessDialog(false)}
                style={{
                  ...styles.submitBtn,
                  width: "100%",
                  justifyContent: "center",
                }}
              >
                OK
              </button>
            </div>
          </div>
        )}

        <style>{`
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          .careers-accordion-body ul { padding-left: 18px; margin: 8px 0; }
          .careers-accordion-body li { margin-bottom: 6px; }
          .careers-accordion-body p { margin-bottom: 10px; }
          @media (max-width: 576px) {
            .careers-modal { margin: 12px !important; max-height: 95vh !important; }
          }
        `}</style>
      </section>
    </>
  );
}

/* ── Inline styles ─────────────────────────────────────────────── */
const styles = {
  sectionTitle: {
    fontSize: 22,
    fontWeight: 600,
    color: "#222",
    marginBottom: 20,
    paddingBottom: 12,
    borderBottom: "2px solid #ff6600",
    display: "inline-block",
  },
  centerBox: {
    textAlign: "center",
    padding: "60px 20px",
  },
  accordionList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginBottom: 40,
  },
  accordionCard: {
    border: "1px solid #e0e0e0",
    borderRadius: 10,
    overflow: "hidden",
    transition: "border-color 0.2s ease",
  },
  accordionHeader: {
    width: "100%",
    background: "white",
    border: "none",
    padding: "16px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    gap: 12,
    textAlign: "left",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    minWidth: 0,
  },
  jobIconBox: {
    width: 38,
    height: 38,
    borderRadius: 10,
    backgroundColor: "#fff3eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  jobTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: "#222",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexShrink: 0,
  },
  badge: {
    fontSize: 11,
    padding: "3px 10px",
    borderRadius: 20,
    backgroundColor: "#fff3eb",
    color: "#cc5200",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  accordionBody: {
    padding: "4px 20px 22px",
    borderTop: "1px solid #f0f0f0",
    backgroundColor: "#fafafa",
  },
  descLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#888",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    margin: "14px 0 8px",
  },
  descContent: {
    fontSize: 14,
    color: "#555",
    lineHeight: 1.75,
  },
  applyBtn: {
    marginTop: 18,
    display: "inline-block",
    backgroundColor: "#ff6600",
    color: "white",
    border: "none",
    borderRadius: 8,
    padding: "10px 26px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    transition: "background-color 0.2s, transform 0.15s",
  },
  // Modal
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.55)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
    padding: 20,
  },
  modal: {
    backgroundColor: "white",
    borderRadius: 12,
    boxShadow: "0 12px 40px rgba(0,0,0,0.2)",
    maxWidth: 560,
    width: "100%",
    maxHeight: "90vh",
    overflowY: "auto",
  },
  modalHeader: {
    padding: "22px 24px 16px",
    borderBottom: "1px solid #eee",
    position: "sticky",
    top: 0,
    backgroundColor: "white",
    zIndex: 1,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  modalTitle: {
    margin: 0,
    fontSize: 20,
    fontWeight: 600,
    color: "#ff6600",
  },
  modalSub: {
    margin: "6px 0 0",
    color: "#555",
    fontSize: 14,
  },
  closeBtn: {
    background: "none",
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    width: 34,
    height: 34,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
  },
  modalBody: {
    padding: "20px 24px 28px",
  },
  alertBox: {
    padding: "12px 16px",
    marginBottom: 16,
    borderRadius: 8,
    border: "1px solid",
    fontSize: 14,
  },
  formGroup: { marginBottom: 18 },
  formLabel: {
    display: "block",
    marginBottom: 7,
    color: "#333",
    fontSize: 13,
    fontWeight: 600,
  },
  formInput: {
    width: "100%",
    padding: "11px 14px",
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
    transition: "border-color 0.2s",
    boxSizing: "border-box",
  },
  fileDrop: {
    border: "2px dashed #e0e0e0",
    borderRadius: 8,
    padding: "24px 16px",
    textAlign: "center",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  modalFooter: {
    display: "flex",
    gap: 10,
    justifyContent: "flex-end",
    marginTop: 24,
  },
  cancelBtn: {
    padding: "11px 22px",
    backgroundColor: "#f5f5f5",
    color: "#666",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
  },
  submitBtn: {
    padding: "11px 28px",
    backgroundColor: "#ff6600",
    color: "white",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
    transition: "background-color 0.2s",
  },
  successBox: {
    backgroundColor: "white",
    padding: "40px 32px",
    borderRadius: 12,
    maxWidth: 400,
    width: "90%",
    textAlign: "center",
  },
  successIcon: {
    width: 76,
    height: 76,
    borderRadius: "50%",
    backgroundColor: "#d4edda",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "0 auto 20px",
  },
};

export default Careers;
